import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { deskApi } from '@/api';

import { getApiBaseUrl } from '@/config/env';

import { PageHeader } from '@/components/layout/PageHeader';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { FormGrid } from '@/components/ui/FormGrid';
import { TimezoneSelect } from '@/components/selects';

import type { DeskBusinessCalendar, DeskHoliday, DeskMailbox, DeskPriority, DeskStatus, DeskTeam } from '@/types/desk';

import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';



const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];



export function DeskSetupPage() {

  const [statuses, setStatuses] = useState<DeskStatus[]>([]);

  const [priorities, setPriorities] = useState<DeskPriority[]>([]);

  const [teams, setTeams] = useState<DeskTeam[]>([]);

  const [mailbox, setMailbox] = useState<DeskMailbox | null>(null);

  const [calendar, setCalendar] = useState<DeskBusinessCalendar | null>(null);
  const [calendarName, setCalendarName] = useState('');
  const [calendarTimezone, setCalendarTimezone] = useState('UTC');
  const [workStart, setWorkStart] = useState('09:00');
  const [workEnd, setWorkEnd] = useState('17:00');

  const [holidays, setHolidays] = useState<DeskHoliday[]>([]);

  const [holidayDate, setHolidayDate] = useState('');

  const [syncing, setSyncing] = useState(false);



  const load = () => {

    Promise.all([

      deskApi.listStatuses(),

      deskApi.listPriorities(),

      deskApi.listTeams(),

      deskApi.getMailbox().catch(() => null),

      deskApi.getBusinessCalendar().catch(() => null),

      deskApi.listHolidays().catch(() => [] as DeskHoliday[]),

    ])

      .then(([s, p, t, m, c, h]) => {

        setStatuses(s);

        setPriorities(p);

        setTeams(t);

        setMailbox(m);

        setCalendar(c);
        if (c) {
          setCalendarName(c.name ?? '');
          setCalendarTimezone(c.timezone ?? 'UTC');
          setWorkStart(c.workStart ?? '09:00');
          setWorkEnd(c.workEnd ?? '17:00');
        }

        setHolidays(h);

      })

      .catch((err) => showApiError(err, 'Failed to load desk setup'));

  };



  useEffect(() => {

    load();

  }, []);



  async function saveCalendar(e: FormEvent<HTMLFormElement>) {

    e.preventDefault();

    const fd = new FormData(e.currentTarget);

    const workingDays = WEEKDAYS.filter((d) => fd.get(`day-${d}`) === 'on');

    try {

      await deskApi.updateBusinessCalendar({
        name: calendarName || undefined,
        timezone: calendarTimezone || 'UTC',
        workingDays,
        workStart,
        workEnd,
      });

      load();

    } catch (err) {

      showApiError(err, 'Failed to update business calendar');

    }

  }



  async function addHoliday(e: FormEvent<HTMLFormElement>) {

    e.preventDefault();

    const fd = new FormData(e.currentTarget);

    try {

      await deskApi.addHoliday({

        holidayDate: String(fd.get('holidayDate') ?? ''),

        name: String(fd.get('name') ?? '') || undefined,

      });

      e.currentTarget.reset();

      setHolidayDate('');

      load();

    } catch (err) {

      showApiError(err, 'Failed to add holiday');

    }

  }



  async function saveImap(e: FormEvent<HTMLFormElement>) {

    e.preventDefault();

    const fd = new FormData(e.currentTarget);

    try {

      await deskApi.updateMailboxImap({

        imapHost: String(fd.get('imapHost') ?? '') || undefined,

        imapPort: fd.get('imapPort') ? Number(fd.get('imapPort')) : undefined,

        imapUsername: String(fd.get('imapUsername') ?? '') || undefined,

        imapPassword: String(fd.get('imapPassword') ?? '') || undefined,

        syncEnabled: fd.get('syncEnabled') === 'on',

      });

      notify.success('IMAP settings saved');

      load();

    } catch (err) {

      showApiError(err, 'Failed to save IMAP settings');

    }

  }



  async function syncNow() {

    setSyncing(true);

    try {

      const r = await deskApi.syncMailbox();

      notify.success(`Processed ${r.processed} inbound messages`);

      load();

    } catch (err) {

      showApiError(err, 'Sync failed');

    } finally {

      setSyncing(false);

    }

  }



  const portalUrl =
    mailbox?.portalLoginUrl != null
      ? `${typeof window !== 'undefined' ? window.location.origin : ''}${mailbox.portalLoginUrl}`
      : '/portal/login';

  const apiBase = getApiBaseUrl();

  return (

    <div>

      <PageHeader

        title="Desk Setup"

        description="Statuses, priorities, teams, inbound mailbox, IMAP polling, customer portal, business hours"

        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Desk', to: '/desk' }, { label: 'Setup' }]}

        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/desk/setup/workflows">
              <Button variant="secondary">Workflow designer</Button>
            </Link>
            <Link to="/desk/canned-responses">
              <Button variant="secondary">Canned responses</Button>
            </Link>
          </div>
        }

      />

      <div className="grid gap-4 lg:grid-cols-3 mb-4">

        <SetupList title="Statuses" items={statuses.map((s) => `${s.name} (${s.code})`)} />

        <SetupList title="Priorities" items={priorities.map((p) => `${p.name} (${p.code})`)} />

        <SetupList title="Teams" items={teams.map((t) => `${t.name} (${t.code})`)} />

      </div>



      <div className="grid gap-4 lg:grid-cols-2">

        <div className="panel">

          <h3 className="font-semibold text-slate-800 mb-2">Inbound mailbox</h3>

          {mailbox ? (

            <dl className="text-sm space-y-1">

              <div>

                <dt className="text-slate-500 inline">Address: </dt>

                <dd className="inline font-mono">{mailbox.emailAddress}</dd>

              </div>

              <div>

                <dt className="text-slate-500 inline">Webhook token: </dt>

                <dd className="inline font-mono text-xs break-all">{mailbox.inboundToken}</dd>

              </div>

              {mailbox.lastSyncAt && (

                <div>

                  <dt className="text-slate-500 inline">Last IMAP sync: </dt>

                  <dd className="inline">{mailbox.lastSyncAt}</dd>

                </div>

              )}

              <p className="text-xs text-slate-500 mt-2">
                POST {apiBase}/v1/desk/email/inbound with header <code>X-Desk-Inbound-Token</code> set to the webhook
                token above.
              </p>

            </dl>

          ) : (

            <p className="text-sm text-slate-500">Mailbox will be provisioned on first access</p>

          )}

        </div>



        <div className="panel">

          <h3 className="font-semibold text-slate-800 mb-2">Customer portal</h3>

          <p className="text-sm text-slate-600 mb-2">Share this link with customers for magic-link sign-in:</p>

          <code className="block text-xs break-all bg-slate-50 p-2 rounded">{portalUrl}</code>

        </div>



        <div className="panel lg:col-span-2">

          <h3 className="font-semibold text-slate-800 mb-2">IMAP polling (support inbox)</h3>

          <form className="grid gap-2 md:grid-cols-2 text-sm" onSubmit={saveImap}>

            <Input name="imapHost" label="IMAP host" defaultValue={mailbox?.imapHost} placeholder="IMAP host" />

            <Input

              name="imapPort"

              type="number"

              label="Port"

              defaultValue={mailbox?.imapPort ?? 993}

              placeholder="Port"

            />

            <Input name="imapUsername" label="Username" defaultValue={mailbox?.imapUsername} placeholder="Username" />

            <Input name="imapPassword" type="password" label="Password" placeholder="Password (app password)" />

            <label className="flex items-center gap-2 md:col-span-2">

              <input name="syncEnabled" type="checkbox" defaultChecked={mailbox?.syncEnabled ?? false} />

              Enable scheduled IMAP sync (every 3 min)

            </label>

            <div className="flex gap-2 md:col-span-2">

              <Button type="submit">Save IMAP</Button>

              <Button type="button" variant="secondary" disabled={syncing} onClick={() => void syncNow()}>

                {syncing ? 'Syncing…' : 'Sync now'}

              </Button>

            </div>

          </form>

        </div>



        <div className="panel">

          <h3 className="font-semibold text-slate-800 mb-2">Business calendar</h3>

          <form className="text-sm space-y-2" onSubmit={saveCalendar}>

            <Input name="name" label="Calendar name" value={calendarName} onChange={(e) => setCalendarName(e.target.value)} placeholder="Calendar name" />

            <TimezoneSelect
              label="Timezone"
              value={calendarTimezone}
              onChange={setCalendarTimezone}
              required
            />

            <div className="flex flex-wrap gap-2">

              {WEEKDAYS.map((d) => (

                <label key={d} className="flex items-center gap-1 text-xs">

                  <input

                    name={`day-${d}`}

                    type="checkbox"

                    defaultChecked={calendar?.workingDays?.includes(d) ?? ['MON', 'TUE', 'WED', 'THU', 'FRI'].includes(d)}

                  />

                  {d}

                </label>

              ))}

            </div>

            <div className="flex gap-2">

              <Input type="time" label="Work start" value={workStart} onChange={(e) => setWorkStart(e.target.value)} className="flex-1" />

              <Input type="time" label="Work end" value={workEnd} onChange={(e) => setWorkEnd(e.target.value)} className="flex-1" />

            </div>

            <Button type="submit">Save calendar</Button>

          </form>

        </div>



        <div className="panel">

          <h3 className="font-semibold text-slate-800 mb-2">Holidays</h3>

          <ul className="text-sm space-y-1 mb-3">

            {holidays.map((h) => (

              <li key={h.id}>

                {h.holidayDate} — {h.name ?? 'Holiday'}

              </li>

            ))}

            {holidays.length === 0 && <li className="text-slate-500">No holidays configured</li>}

          </ul>

          <form className="text-sm" onSubmit={addHoliday}>

            <FormGrid columns={2} className="items-end">

              <DatePicker name="holidayDate" label="Date" value={holidayDate} onChange={setHolidayDate} required />

              <Input name="name" label="Name" placeholder="Name" />

              <Button type="submit">Add holiday</Button>

            </FormGrid>

          </form>

        </div>

      </div>

    </div>

  );

}



function SetupList({ title, items }: { title: string; items: string[] }) {

  return (

    <div className="panel">

      <h3 className="font-semibold text-slate-800 mb-2">{title}</h3>

      <ul className="text-sm space-y-1">

        {items.map((item) => (

          <li key={item}>{item}</li>

        ))}

        {items.length === 0 && <li className="text-slate-500">Defaults provisioned on first use</li>}

      </ul>

    </div>

  );

}

